import type { ReportColumn } from '../models/column.types'
import type { ScheduleTabMap, ScheduleTabValue } from '../models/schedule.types'
import type {
  ReportTableAdditionalElementV1,
  ReportTableConfigV1,
  ReportTableDropdownV1,
  ReportTablePreferenceV1,
  ReportTableSortDirection,
  ReportTableSortV1,
  ResolvedReportTableConfig,
} from '../models/tableConfig.types'

const DEFAULT_CONFIG: ResolvedReportTableConfig = {
  version: 1,
  source: 'local',
  defaultSort: null,
  allowColumnReorder: true,
  allowColumnResize: true,
  requiredFields: [],
  sortCycle: 'triState',
  initialSortDirection: 'desc',
  dropdowns: [],
  additionalElements: [],
}
const MIN_COLUMN_WIDTH = 48
const MAX_COLUMN_WIDTH = 1_200
const CONFIG_ID = /^[a-z][a-z0-9-]*$/u

interface ParsedConfig {
  defaultSort?: ReportTableSortV1 | null
  allowColumnReorder?: boolean
  allowColumnResize?: boolean
  requiredFields?: string[]
  sortCycle?: 'binary' | 'triState'
  initialSortDirection?: ReportTableSortDirection
  dropdowns?: ReportTableDropdownV1[]
  additionalElements?: ReportTableAdditionalElementV1[]
}

interface ParsedPreference {
  columnOrder?: string[]
  columnWidths?: Record<string, number>
  sort?: ReportTableSortV1 | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const id = value.trim()
  return CONFIG_ID.test(id) ? id : null
}

function parseSort(value: unknown): ReportTableSortV1 | null {
  if (!isRecord(value) || typeof value.field !== 'string') return null
  const field = value.field.trim()
  if (!field || (value.order !== 1 && value.order !== -1)) return null
  return { field, order: value.order }
}

function parseDropdown(value: unknown): ReportTableDropdownV1 | null {
  if (!isRecord(value) || value.kind !== 'filter') return null
  const id = normalizeId(value.id)
  const field = typeof value.field === 'string' ? value.field.trim() : ''
  const labelKey = typeof value.labelKey === 'string' ? value.labelKey.trim() : ''
  const mode = value.mode
  const optionsSource = value.optionsSource
  if (
    !id ||
    !field ||
    !labelKey ||
    (mode !== 'single' && mode !== 'multiple') ||
    (optionsSource !== 'host' && optionsSource !== 'schedule-dropdown' && optionsSource !== 'rows')
  )
    return null
  return {
    id,
    kind: 'filter',
    field,
    labelKey,
    mode,
    optionsSource: optionsSource as ReportTableDropdownV1['optionsSource'],
  }
}

function parseAdditionalElement(value: unknown): ReportTableAdditionalElementV1 | null {
  if (!isRecord(value) || value.kind !== 'registered') return null
  const id = normalizeId(value.id)
  return id ? { id, kind: 'registered' } : null
}

function uniqueById<T extends { id: string }>(values: readonly T[]): T[] {
  const seen = new Set<string>()
  return values.filter((value) => {
    if (seen.has(value.id)) return false
    seen.add(value.id)
    return true
  })
}

function uniqueFields(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  const seen = new Set<string>()
  return value.flatMap((item) => {
    if (typeof item !== 'string') return []
    const field = item.trim()
    if (!field || seen.has(field)) return []
    seen.add(field)
    return [field]
  })
}

function parseConfig(value: unknown): ParsedConfig | null {
  if (!isRecord(value) || value.version !== 1) return null
  const parsed: ParsedConfig = {}
  if ('defaultSort' in value) {
    if (value.defaultSort === null) parsed.defaultSort = null
    else {
      const sort = parseSort(value.defaultSort)
      if (sort) parsed.defaultSort = sort
    }
  }
  if (typeof value.allowColumnReorder === 'boolean')
    parsed.allowColumnReorder = value.allowColumnReorder
  if (typeof value.allowColumnResize === 'boolean')
    parsed.allowColumnResize = value.allowColumnResize
  const requiredFields = uniqueFields(value.requiredFields)
  if (requiredFields) parsed.requiredFields = requiredFields
  if (value.sortCycle === 'binary' || value.sortCycle === 'triState')
    parsed.sortCycle = value.sortCycle
  if (value.initialSortDirection === 'asc' || value.initialSortDirection === 'desc')
    parsed.initialSortDirection = value.initialSortDirection
  if (Array.isArray(value.dropdowns))
    parsed.dropdowns = uniqueById(value.dropdowns.flatMap((item) => parseDropdown(item) ?? []))
  if (Array.isArray(value.additionalElements))
    parsed.additionalElements = uniqueById(
      value.additionalElements.flatMap((item) => parseAdditionalElement(item) ?? []),
    )
  return parsed
}

/** Local defaults are authoritative today; a valid schedule-tab v1 can replace individual fields. */
export function resolveReportTableConfig(
  localConfig?: ReportTableConfigV1,
  scheduleTabConfig?: unknown,
): ResolvedReportTableConfig {
  const local = parseConfig(localConfig) ?? {}
  const backend = parseConfig(scheduleTabConfig)
  const merged = { ...local, ...(backend ?? {}) }
  return {
    ...DEFAULT_CONFIG,
    ...merged,
    source: backend ? 'schedule-tab' : 'local',
    requiredFields: merged.requiredFields ?? [],
    dropdowns: merged.dropdowns ?? [],
    additionalElements: merged.additionalElements ?? [],
  }
}

function tabRecord(value: ScheduleTabValue): Record<string, unknown> | null {
  return isRecord(value) ? value : null
}

/** Future wire seam: `schedule.additional_info.tabs[*].table_config`. */
export function findScheduleTabTableConfig(
  tabs: ScheduleTabMap | undefined,
  partitionName: string | undefined,
): unknown {
  if (!tabs || !partitionName) return undefined
  for (const [tabKey, value] of Object.entries(tabs)) {
    const tab = tabRecord(value)
    if (
      tab &&
      (tab.partition_name === partitionName || tabKey === partitionName) &&
      'table_config' in tab
    )
      return tab.table_config
  }
  return undefined
}

function parsePreference(value: unknown): ParsedPreference | null {
  if (!isRecord(value) || value.version !== 1) return null
  const parsed: ParsedPreference = {}
  if (Array.isArray(value.columnOrder))
    parsed.columnOrder = value.columnOrder.filter(
      (field): field is string => typeof field === 'string',
    )
  if (isRecord(value.columnWidths)) {
    parsed.columnWidths = Object.fromEntries(
      Object.entries(value.columnWidths).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1]),
      ),
    )
  }
  if ('sort' in value) {
    if (value.sort === null) parsed.sort = null
    else {
      const sort = parseSort(value.sort)
      if (sort) parsed.sort = sort
    }
  }
  return parsed
}

function isSortable(columns: readonly ReportColumn[], field: string): boolean {
  return columns.some((column) => column.field === field && column.sortable !== false)
}

function clampWidth(column: ReportColumn, width: number): number {
  return Math.min(
    column.maxWidth ?? MAX_COLUMN_WIDTH,
    Math.max(column.minWidth ?? MIN_COLUMN_WIDTH, width),
  )
}

/** Reconcile cached interaction state with the current catalog/config; malformed data fails closed. */
export function resolveReportTablePreference(
  value: unknown,
  columns: readonly ReportColumn[],
  config: ResolvedReportTableConfig,
): ReportTablePreferenceV1 {
  const parsed = parsePreference(value)
  const available = new Set(columns.map((column) => column.field))
  const seen = new Set<string>()
  const savedOrder = parsed?.columnOrder ?? []
  const columnOrder = [...savedOrder, ...columns.map((column) => column.field)].filter((field) => {
    if (!available.has(field) || seen.has(field)) return false
    seen.add(field)
    return true
  })
  const columnWidths = Object.fromEntries(
    columns.flatMap((column) => {
      const width = parsed?.columnWidths?.[column.field]
      return width === undefined ? [] : [[column.field, clampWidth(column, width)] as const]
    }),
  )
  const preferredSort = parsed && 'sort' in parsed ? parsed.sort : config.defaultSort
  const sort = preferredSort && isSortable(columns, preferredSort.field) ? preferredSort : null
  return { version: 1, columnOrder, columnWidths, sort }
}

/** Apply presentation preferences without mutating catalog-owned column objects. */
export function applyReportTablePreference(
  columns: readonly ReportColumn[],
  preference: ReportTablePreferenceV1,
): ReportColumn[] {
  const order = new Map(preference.columnOrder.map((field, index) => [field, index]))
  return columns
    .map((column, index) => ({ column, index }))
    .sort(
      (left, right) =>
        (order.get(left.column.field) ?? columns.length + left.index) -
        (order.get(right.column.field) ?? columns.length + right.index),
    )
    .map(({ column }) => ({
      ...column,
      ...(preference.columnWidths[column.field] === undefined
        ? {}
        : { width: preference.columnWidths[column.field] }),
    }))
}
